// This file be kept in sync with the billingUtils.ts file in the static package
import type {
  BillingIntervals,
  BillingProcess,
  LegacySubscription,
  LimitNames,
  ModernSubscription,
  PlanLimitsType,
  PlanTypes,
  schemas,
} from '@gonfalon/openapi/types';
import type { SeriesListRep } from '@gonfalon/rest-api/types';
import type { UseQueryResult } from '@tanstack/react-query';

export type LimitPlanList = Omit<schemas['LimitPlanListRep'], 'plans'> & { plans?: { [k in LimitNames]?: LimitPlan } };

export type Tier = schemas['TierRep'];
export type LimitPlan = schemas['LimitPlanRep'];

export type SubscriptionUsage = Omit<schemas['SubscriptionUsageRep'], '_usage'> &
  { _usage?: { [k in LimitNames]?: number | null } }['_usage'];

// A single billing limit from the backend's `_limitDetails`, already sorted in
// display order. Same shape as the generated schema (`limit` and `value` are required),
// but `limit` is narrowed to the LimitNames enum (the backend only ever emits LimitNames
// values). Replaces the frontend BILLING_LIMIT_ORDER constant as the source of truth for
// which limits to render and in what order.
export type LimitDetail = Omit<schemas['LimitDetail'], 'limit'> & { limit: LimitNames };

// Once the SubscriptionRep has the _next field returning from the spec, update this
export type ScheduledSubscriptionChange = {
  effectiveDate: string;
  updatedAt: string;
  primaryContext: string;
  limits: { [k in LimitNames]: number };
};

export type SubscriptionChangeType = {
  planType: PlanTypes | null;
  billingInterval: BillingIntervals | null;
  billingProcess: BillingProcess | null;
  limits: PlanLimitsType;
};

export type SubscriptionChange = SubscriptionChangeType & {
  areLimitsEmpty(): boolean;
  areLimitsZero(): boolean;
  hasSSO(): boolean;
  getSeatCount(): number | null;
  isYearly(): boolean;
  isMonthly(): boolean;
  isDeveloperPlan(): boolean;
  isFoundationPlan(): boolean;
  isProfessionalPlan(): boolean;
  isEnterprisePlan(): boolean;
  isGuardianPlan(): boolean;
};

export enum SubscriptionStates {
  ACTIVE_TRIAL = 'ActiveTrial',
  ACTIVE_TRIAL_WITH_SUBSCRIPTION = 'ActiveTrialWithSubscription',
  GRACE_PERIOD_TRIAL = 'GracePeriodTrial',
  LAPSED_TRIAL = 'LapsedTrial',
  ACTIVE_SUBSCRIPTION = 'ActiveSubscription',
  FAILED_PAYMENT_SUBSCRIPTION = 'FailedPaymentSubscription',
  PENDING_CANCELED_SUBSCRIPTION = 'PendingCanceledSubscription',
  CANCELED_SUBSCRIPTION = 'CanceledSubscription',
  LAPSED_SUBSCRIPTION = 'LapsedSubscription',
  USAGE_EXCEEDED_GRACE_PERIOD = 'UsageExceededGracePeriod',
  USAGE_EXCEEDED_READ_ONLY = 'UsageExceededReadOnly',
  UNKNOWN = '**Immutable.js Record default only**',
}

export type { LegacySubscription, ModernSubscription, PlanLimitsType } from '@gonfalon/openapi/types';
export {
  PlanTypes,
  BillingIntervals,
  BillingProcess,
  LimitNames,
  SubscriptionTypes,
  MarketplaceProviders,
} from '@gonfalon/openapi/types';

// union type for all subscription types
export type Subscription = ModernSubscription | LegacySubscription;

export type MainCancelReason =
  | 'time'
  | 'getting-started'
  | 'capabilities'
  | 'team-adoption'
  | 'tool'
  | 'price'
  | 'other';

export type CancelFormFields = { mainCancelReason?: MainCancelReason; customCancelReason?: string };

export type BillingEventInfo = {
  daysLeftInTrial?: number;
  error?: string;
  termsVersion?: string;
  component?: string;
  cta?: string;
  isNewPlanPicker?: boolean;
  price?: number | null;
  planType?: string;
  oldPlan?: unknown;
  seatCount?: number;
  hasSSO?: boolean;
} & CancelFormFields;

export enum AggregationType {
  MONTH_TO_DATE = 'month_to_date',
  INCREMENTAL = 'incremental',
  ROLLING_30DAY = 'rolling_30d',
  AVERAGE = 'average',
}

export enum DimensionType {
  ANONYMOUS_V2 = 'anonymousV2',
  CONNECTION_TYPE = 'connectionType',
  CONTEXT_KIND = 'contextKind',
  DESTINATION = 'destination',
  ENVIRONMENT_ID = 'environmentId',
  EVENT_KIND = 'eventKind',
  EVENT_KEY = 'eventKey',
  EXPERIMENT_ID = 'experimentId',
  PROJECT_ID = 'projectId',
  RELAY_VERSION = 'relayVersion',
  SDK_APP_ID = 'sdkAppId',
  SDK_NAME = 'sdkName',
  SDK_TYPE = 'sdkType',
  SDK_VERSION = 'sdkVersion',
  SDK_VERSION_V1 = 'sdk', // Query by SDK in v1 usage APIs
}

export enum UsageTab {
  OVERVIEW = 'overview',
  SERVICE_CONNECTIONS = 'service-connections',
  CLIENT_SIDE_MAU = 'clientside-mau',
  EXPERIMENTATION_KEYS = 'experimentation-keys',
  EXPERIMENTATION_EVENTS = 'experimentation-events',
  EXPERIMENTATION_MAU = 'experimentation-mau',
  DATA_EXPORT = 'data-export',
  WAREHOUSE_DATA_EXPORT = 'warehouse-data-export',
  SESSIONS = 'sessions',
  ERRORS = 'errors',
  LOGS = 'logs',
  TRACES = 'traces',
  TOTAL_CONTEXTS = 'total-contexts',
  CLIENT_SIDE_CONTEXTS = 'clientside-contexts',
  SERVER_SIDE_CONTEXTS = 'serverside-contexts',
  METRICS = 'metrics',
  VEGA_AI = 'vega-ai',
  AI_RUNS = 'ai-runs',
  CLIENT_CONNECTIONS = 'client-connections',
  PEAK_CONCURRENT_CONNECTIONS = 'peak-concurrent-connections',
}

export type UsageTabKey = Exclude<UsageTab, UsageTab.OVERVIEW>;
export type DiagnosticTabKey =
  | UsageTab.TOTAL_CONTEXTS
  | UsageTab.CLIENT_SIDE_CONTEXTS
  | UsageTab.SERVER_SIDE_CONTEXTS
  | UsageTab.METRICS
  | UsageTab.VEGA_AI
  | UsageTab.CLIENT_CONNECTIONS
  | UsageTab.PEAK_CONCURRENT_CONNECTIONS
  | UsageTab.WAREHOUSE_DATA_EXPORT;

export type MonthOption = {
  key: string;
  label: string;
};

/**
 * Represents a single data point in a time series.
 * - time: Unix timestamp in milliseconds
 * - value: The measurement value, or null for future/unavailable data points
 * - primaryContext: The context kind of data point (e.g. 'user', 'organization', 'device'), or null if unknown
 */
export type SeriesDataItem = {
  time: number;
  value: number | null;
  primaryContext?: string | undefined;
};

/**
 * Time series data as an array of SeriesDataItem objects.
 * Each item contains a timestamp, corresponding value (or null for future/unavailable data), and context.
 */
export type SeriesData = SeriesDataItem[];

/**
 * Narrowed type for SeriesListRep.series
 *
 * Each element of the array is an object with a 'time' field, whose value is the timestamp, and one or more key fields.
 * If there are multiple key fields, they are labeled '0', '1', and so on.
 * Ex: { '0': 11, '1': 15, time: 1677888000000 }
 */
type SeriesTimeSlice = {
  time: number;
} & {
  [key: string]: number | undefined;
};

export type UsageSeriesList = Omit<SeriesListRep, 'series'> & {
  series: SeriesTimeSlice[];
};

export type UsageTabData = {
  data: UseQueryResult<UsageSeriesList, unknown>;
  isLoading: boolean;
  isError: boolean;
  tab: UsageTab;
  limit: number;
  limitName?: LimitNames;
  title: string;
  total: number;
  timestamps: UsageTimestamps;
  precision?: number;
  aggregationType: AggregationType;
};

export type UsageTimestamps = {
  from: number;
  to: number;
  cutoff: number;
};

export enum MeterType {
  SUM = 'sum',
  UNIQUE = 'unique',
  AVERAGE = 'average',
}

export enum TimeRangeOption {
  THREE_DAY = '3day',
  THIRTY_DAY = '30day',
  MONTH_TO_DATE = 'mtd',
  THREE_MONTH = '3month',
}

export enum Granularity {
  DAILY = 'daily',
  HOURLY = 'hourly',
  MONTHLY = 'monthly',
}

export type UsageQueryFnName =
  | 'getServiceConnectionsUsageQuery'
  | 'getMAUClientsideUsageQuery'
  | 'getDataExportEventsUsageQuery'
  | 'getWarehouseExportUsageQuery'
  | 'getExperimentationKeysUsageQuery'
  | 'getExperimentationEventsUsageQuery'
  | 'getMAUTotalUsageQuery'
  | 'getSdkActiveQuery'
  | 'getContextsTotalUsageQuery'
  | 'getContextsClientsideUsageQuery'
  | 'getContextsServersideUsageQuery'
  | 'getObservabilitySessionsUsageQuery'
  | 'getObservabilityErrorsUsageQuery'
  | 'getObservabilityLogsUsageQuery'
  | 'getObservabilityTracesUsageQuery'
  | 'getObservabilityMetricsUsageQuery'
  | 'getVegaAIUsageQuery'
  | 'getAIRunsUsageQuery'
  | 'getClientConnectionsUsageQuery'
  | 'getPeakConcurrentConnectionsUsageQuery'
  | 'getPeakConcurrentConnectionsUsageBySdkVersionQuery';
